import { RootState } from "../index";
import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { createApp, fetchApp, fetchApps, updateApp } from "../../actions/community/appsActions";

export interface AppInterface {
  url: string;
  main_bundle_url: string | undefined;

  id: number;

  uid: string;
  jwt_secret: string;

  setting: EntityId,

  name: string;
  notes: string | null;

  email: string | null;
  email_name: string | null;
  email_template: string | null;

  main_bundle_id: EntityId | null;

  bundle_name: string | null;
  bundle_icon_url: string | null;
  bundle_store_url: string | null;

  icon_url: string;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: AppInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const appsAdapter = createEntityAdapter<AppInterface>();

const slice = createSlice({
  name: 'apps',
  initialState: appsAdapter.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchApps.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchApps.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.apps;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchApps.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchApp.fulfilled, (state, action) => {
        const app = Object.values(action.payload.app)[0];
        state.entities[app.id] = app;
      })

      // #create
      .addCase(createApp.fulfilled, (state, action) => {
        const app = Object.values(action.payload.app)[0];
        state.entities[app.id] = app;
        state.ids.unshift(app.id);
      })
      .addCase(createApp.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateApp.fulfilled, (state, action) => {
        const app = Object.values(action.payload.app)[0];
        state.entities[app.id] = app;
      })
      .addCase(updateApp.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setNewParams,
  setNewError,
  setEditId,
  setEditError
} = slice.actions;

export const {
  selectById: selectAppById,
  selectIds: selectAppIds,
  selectEntities: selectAppEntities
} = appsAdapter.getSelectors<RootState>(state => state.community.apps);
